import { isBookingButton, loadUtmParams } from "./utilities";

const SESSION_STORAGE_KEY = "ac_booking_sid";

const BOOKING_SESSION_ID_PARAM = "ac_booking_sid";

/**
 * Request header carrying the booking session id (ac_booking_sid) on booking API calls, so the
 * server can correlate request telemetry with the frontend BookingStep events.
 * Keep in sync with BookingSessionTelemetryInitializer on the server.
 */
export const BOOKING_SESSION_ID_HEADER = "X-Booking-Session-Id";

export const generateBookingSessionId = (): string => {
  const timestamp = Math.floor(Date.now() / 1000);
  const random = Math.floor(Math.random() * 2147483647);

  return `s_${random}.${timestamp}`;
};

export const getBookingSessionIdFromElement = (
  element: HTMLElement
): string | undefined => element.dataset.bookingSid;

export const getBookingSessionIdFromStorage = (): string | undefined => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

export const saveBookingSessionIdToStorage = (id: string): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    // sessionStorage unavailable
  }
};

export const getOrCreateBookingSessionId = (): string => {
  let sessionId = getBookingSessionIdFromStorage();

  if (!sessionId) {
    sessionId = generateBookingSessionId();
    saveBookingSessionIdToStorage(sessionId);
  }

  return sessionId;
};

const decorateBookingLinks = (root: HTMLElement | Document): void => {
  const utmParams = loadUtmParams();

  root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach(bookingButton => {
    if (isBookingButton(bookingButton)) {
      const bookingSessionId = generateBookingSessionId();

      bookingButton.dataset.bookingSid = bookingSessionId;

      const href = bookingButton.getAttribute("href");

      if (href) {
        try {
          const url = new URL(href, window.location.href);
          const isExternal = url.origin !== window.location.origin;

          if (isExternal) {
            // Add booking session id as query parameter for external links
            url.searchParams.set(BOOKING_SESSION_ID_PARAM, bookingSessionId);

            if (utmParams?.channel) {
              url.searchParams.set("utm_channel", utmParams.channel);
            }
            if (utmParams?.medium) {
              url.searchParams.set("utm_medium", utmParams.medium);
            }
            if (utmParams?.source) {
              url.searchParams.set("utm_source", utmParams.source);
            }

            bookingButton.setAttribute("href", url.toString());
          }
        } catch {
          // invalid URL, skip
        }
      }
    }
  });
};

export const initializeBookingSessionId = (): void => {
  htmx.onLoad(root => {
    decorateBookingLinks(root);
  });
};
